import { useTranslation } from 'react-i18next'
import { ConfirmModal } from '@/components/ConfirmModal'
import { UserAction, UserType } from './types'

export interface UserActionModalProps {
  isOpen: boolean
  action: UserAction | null
  userType: UserType
  userName?: string
  isLoading?: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Generic modal for user actions (suspend, delete, etc.)
 * Adapts messages based on action and user type
 */
export default function UserActionModal({
  isOpen,
  action,
  userType,
  userName = '',
  isLoading = false,
  onConfirm,
  onCancel,
}: UserActionModalProps) {
  const { t } = useTranslation()

  const getModalContent = () => {
    const userLabel = userType === 'organizer' ? 'organisateur' : 'chanteur'
    const userCapital = userType === 'organizer' ? 'Organisateur' : 'Chanteur'

    switch (action) {
      case 'suspend':
        return {
          title: t('superadmin.suspend'),
          message: `${t('common.confirmAction')} Cet ${userLabel} sera suspendu et ne pourra plus accéder à son compte.${userName ? ` (${userName})` : ''}`,
          confirmLabel: t('superadmin.suspend'),
          variant: 'warning' as const,
        }
      case 'unsuspend':
        return {
          title: t('common.restore'),
          message: `${t('common.confirmAction')} Cet ${userLabel} retrouvera accès à son compte.${userName ? ` (${userName})` : ''}`,
          confirmLabel: t('common.restore'),
          variant: 'primary' as const,
        }
      case 'delete':
        return {
          title: t('common.delete'),
          message: `${t('common.confirmAction')} Cette action est irréversible. Tous les ${userType === 'organizer' ? 'événements et les données' : 'événements'} de cet ${userLabel} seront supprimés.${userName ? ` (${userName})` : ''}`,
          confirmLabel: t('common.delete'),
          variant: 'danger' as const,
        }
      case 'reset-password':
        return {
          title: 'Réinitialiser le mot de passe',
          message: `${t('common.confirmAction')} Un email de réinitialisation sera envoyé à cet ${userLabel}.${userName ? ` (${userName})` : ''}`,
          confirmLabel: 'Envoyer',
          variant: 'primary' as const,
        }
      default:
        return {
          title: '',
          message: '',
          confirmLabel: '',
          variant: 'primary' as const,
        }
    }
  }

  const content = getModalContent()

  return (
    <ConfirmModal
      isOpen={isOpen}
      title={content.title}
      message={content.message}
      confirmLabel={content.confirmLabel}
      cancelLabel={t('common.cancel')}
      variant={content.variant}
      isLoading={isLoading}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  )
}

